import { useEffect, useMemo, useState } from 'react';

import {
  LayoutDashboard,
  BarChart3,
  Target,
  CalendarClock,
  ClipboardList,
  FileText,
  FilePen,
  Briefcase,
  Cake,
  Users as UsersIcon,
  Share2,
  Trophy,
  DollarSign,
  ShoppingBag,
  Megaphone,
  GraduationCap,
  HelpCircle,
  Globe,
  Menu,
  X,
  ChevronLeft,
  ShieldCheck,
  ClipboardCheck,
  UserRound,
  Truck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, NavLink, useLocation } from 'react-router-dom';

import { useConsents } from '@alamo/api';
import { useAuth, usePermissions } from '@alamo/auth';
import { Badge, SidebarFlyout, cn } from '@alamo/ui';

import { AnnouncementLoginCarousel } from '../components/AnnouncementLoginCarousel';
import { DataReconfirmationProvider } from '../components/DataReconfirmation/DataReconfirmationProvider';
import { InstallAppPrompt } from '../components/InstallAppPrompt';
import { ReacceptanceModal } from '../components/ReacceptanceModal/ReacceptanceModal';
import { TourProvider } from '../components/Tour/TourProvider';
import { PreApprovalBanner } from '../pages/PreApproval/PreApprovalBanner';
import { REQUIRED_CONSENT_PURPOSES } from '../pages/Privacy/consent-config';

import { BreadcrumbProvider } from './components/BreadcrumbContext';
import { BreadcrumbHost } from './components/BreadcrumbHost';
import { LogoSvg } from './components/LogoSvg';
import { NotificationBellDropdown } from './components/NotificationBellDropdown';
import {
  SidebarAdminSubGroups,
  useHasVisibleAdminGroups,
} from './components/SidebarAdminSubGroups';
import { UserMenuDropdown } from './components/UserMenuDropdown';

const SIDEBAR_COLLAPSED_KEY = 'alamo_sidebar_collapsed';

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  permission?: { resource: string; action: string };
  /**
   * Roles that must NOT see this item even if the permission check passes.
   * Used for resources that cross-cut permission grants but belong to a
   * different surface for some roles (e.g. admin_alamo uses /admin/network
   * instead of /team because admins have no consultant_profile).
   */
  hiddenForRoles?: string[];
  /**
   * Consultant types that must NOT see this item (e.g. CLT consultors do
   * not participate in MLM — they never see /team, /commissions, /ranking,
   * /referrals). Filtered against the user's consultant_type field from
   * GET /auth/me.
   */
  hiddenForConsultantTypes?: Array<'clt' | 'pf' | 'pj'>;
  /**
   * Show this item ONLY while the user is not approved yet (`!isApproved`
   * from useAuth). Used for surfaces that active users reach elsewhere
   * (Perfil via user menu; /my-registration redirects to /dashboard when
   * active).
   */
  preApprovalOnly?: boolean;
  /**
   * Hide this item when the user is not eligible to receive commission
   * (`commission_eligible === false` from GET /auth/me). Covers both CLT and
   * employer-absorbed consultants without per-type hardcoding (Phase 32).
   */
  hiddenWhenCommissionIneligible?: boolean;
  /**
   * Optional trailing badge rendered next to the label (expanded sidebar
   * only). Use `coming_soon` for stub destinations so users know the link
   * works but the feature is still being built — matches the ComingSoonPage
   * inside each stub.
   */
  badge?: 'coming_soon';
}

interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Build navigation sections. Each section has a label (i18n key) and items.
 * Items are filtered by permission at render time.
 */
function buildSections(t: (key: string) => string): NavSection[] {
  return [
    {
      label: t('sidebar.section_inicio'),
      items: [
        {
          label: 'nav.dashboard',
          path: '/dashboard',
          icon: LayoutDashboard,
          permission: { resource: 'dashboard', action: 'read' },
        },
        {
          label: 'nav.my_registration',
          path: '/my-registration',
          icon: ClipboardCheck,
          permission: { resource: 'profile', action: 'read' },
          preApprovalOnly: true,
        },
        {
          label: 'nav.profile',
          path: '/profile',
          icon: UserRound,
          permission: { resource: 'profile', action: 'read' },
          preApprovalOnly: true,
        },
      ],
    },
    {
      label: t('sidebar.section_trabalho'),
      items: [
        {
          label: 'nav.quotes',
          path: '/quotes',
          icon: FileText,
          permission: { resource: 'quotation', action: 'read' },
        },
        {
          label: 'nav.fleet_quotations',
          path: '/admin/fleet-quotations',
          icon: Truck,
          permission: { resource: 'fleet_quotations', action: 'manage' },
        },
        {
          label: 'nav.proposals',
          path: '/proposals',
          icon: ClipboardList,
          permission: { resource: 'proposal', action: 'read' },
        },
        {
          label: 'nav.reminders',
          path: '/agenda',
          icon: CalendarClock,
          permission: { resource: 'dashboard', action: 'read' },
          hiddenForRoles: ['admin_alamo', 'gerente'],
        },
        {
          label: 'nav.competitor_offers',
          path: '/competitor-offers',
          icon: ShieldCheck,
          permission: { resource: 'competitor-offers', action: 'request' },
        },
      ],
    },
    {
      label: t('sidebar.section_carteira'),
      items: [
        {
          label: 'nav.portfolio',
          path: '/portfolio',
          icon: Briefcase,
          permission: { resource: 'portfolio', action: 'read' },
        },
        {
          label: 'nav.birthdays',
          path: '/associates/birthdays',
          icon: Cake,
          permission: { resource: 'portfolio', action: 'read' },
        },
        {
          label: 'nav.endossos',
          path: '/endossos',
          icon: FilePen,
          permission: { resource: 'policies', action: 'read' },
        },
      ],
    },
    {
      label: t('sidebar.section_rede'),
      items: [
        {
          label: 'nav.team',
          path: '/team',
          icon: UsersIcon,
          permission: { resource: 'team', action: 'read' },
          hiddenForRoles: ['admin_alamo'],
        },
        {
          label: 'nav.referrals',
          path: '/team/referrals',
          icon: Share2,
          permission: { resource: 'referrals', action: 'read' },
          hiddenForRoles: ['admin_alamo', 'gerente'],
        },
        {
          label: 'nav.ranking',
          path: '/ranking',
          icon: Trophy,
          permission: { resource: 'ranking', action: 'read' },
        },
        {
          label: 'nav.commissions',
          path: '/commissions',
          icon: DollarSign,
          permission: { resource: 'commissions', action: 'read' },
          hiddenForRoles: ['admin_alamo', 'gerente'],
          hiddenWhenCommissionIneligible: true,
        },
      ],
    },
    {
      label: t('sidebar.section_gestao'),
      items: [
        {
          label: 'nav.metrics',
          path: '/metricas',
          icon: BarChart3,
          permission: { resource: 'dashboard', action: 'read' },
        },
        {
          label: 'nav.goals',
          path: '/metas',
          icon: Target,
          permission: { resource: 'dashboard', action: 'read' },
        },
      ],
    },
    {
      label: t('sidebar.section_recursos'),
      items: [
        {
          label: 'nav.ecommerce',
          path: '/ecommerce',
          icon: ShoppingBag,
          permission: { resource: 'ecommerce', action: 'read' },
        },
        {
          label: 'nav.novidades',
          path: '/novidades',
          icon: Megaphone,
          permission: { resource: 'announcements', action: 'read' },
        },
        {
          label: 'nav.training',
          path: '/training',
          icon: GraduationCap,
          permission: { resource: 'training', action: 'view_onboarding' },
        },
        {
          label: 'nav.meu_site',
          path: '/my-site',
          icon: Globe,
          permission: { resource: 'site', action: 'manage' },
          badge: 'coming_soon',
        },
      ],
    },
    {
      label: t('sidebar.section_ajuda'),
      items: [
        {
          label: 'nav.support',
          path: '/support',
          icon: HelpCircle,
          permission: { resource: 'support', action: 'read' },
        },
      ],
    },
  ];
}

export function AppLayout() {
  const { t } = useTranslation('common');
  const { user, roleName, consultantType, commissionEligible, isApproved } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const { data: consents, isLoading: consentsLoading, isError: consentsError } = useConsents();
  const hasVisibleAdminGroups = useHasVisibleAdminGroups();

  // Gate de consentimento LGPD (opção A): só dispara quando o GET
  // /privacy/consent resolveu com sucesso E um propósito obrigatório está
  // genuinamente ausente/revogado. Um erro transitório NÃO desvia o usuário
  // para /consents — a autorização real é server-side, e o gate re-avalia
  // sozinho quando o consent volta a carregar (fail-open de UX).
  const requiredConsentMissing =
    !consentsLoading &&
    !consentsError &&
    REQUIRED_CONSENT_PURPOSES.some(
      (purpose) =>
        !consents?.some(
          (consent) => consent.purpose === purpose && consent.granted && !consent.revokedAt,
        ),
    );

  // Persist sidebar collapsed state in localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) || 'false') as boolean;
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  // Close mobile drawer on Esc.
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  // Lock body scroll when mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [mobileOpen]);

  const allSections = useMemo(() => buildSections(t), [t]);

  const visibleSections = useMemo(
    () =>
      allSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            // Permission check
            if (item.permission) {
              const permitted = hasPermission(item.permission.resource, item.permission.action);
              if (!permitted) return false;
            }
            // Role-based hide (cross-cuts permission grants)
            if (item.hiddenForRoles?.includes(roleName ?? '')) {
              return false;
            }
            // Consultant-type-based hide (CLT excluded from MLM-only items)
            if (consultantType && item.hiddenForConsultantTypes?.includes(consultantType)) {
              return false;
            }
            // Commission-eligibility hide: only suppress when the backend
            // explicitly reports the user as ineligible (false). Null (no
            // consultant_profile, e.g. admin) keeps the item — admins still
            // gate it via the permission check above.
            if (item.hiddenWhenCommissionIneligible && commissionEligible === false) {
              return false;
            }
            // Pré-aprovação (decisão Pedro 2026-07-09, sobrescreve o allowlist
            // PO 2026-04-17): consultor em análise vê TODOS os módulos que as
            // permissions dão — ele já pode operar (cotar etc.) antes da
            // aprovação; o backend barra apenas rejected/inactive. Os itens
            // preApprovalOnly (Meu Cadastro/Meu Perfil) somam-se aqui e somem
            // no chrome pleno (ativo acessa por outros pontos).
            if (isApproved && item.preApprovalOnly) {
              return false;
            }
            return true;
          }),
        }))
        .filter((section) => section.items.length > 0),
    [allSections, hasPermission, isApproved, roleName, consultantType, commissionEligible],
  );

  // Flat list of every visible nav path. Lets SidebarLink resolve the most
  // specific active item so a parent like /team does not also light up when a
  // more specific child like /team/referrals is the current route.
  const allNavPaths = useMemo(
    () => visibleSections.flatMap((section) => section.items.map((item) => item.path)),
    [visibleSections],
  );

  if (requiredConsentMissing && location.pathname !== '/consents') {
    return <Navigate to="/consents" replace state={{ from: location.pathname }} />;
  }

  return (
    <BreadcrumbProvider>
      <div className="flex h-full bg-slate-50">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            style={{ zIndex: 200 }}
            className={cn(
              'fixed inset-0 bg-secondary-950/60 backdrop-blur-sm md:hidden',
              'transition-opacity duration-200',
            )}
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          data-tour="sidebar"
          style={{ zIndex: 200 }}
          className={cn(
            'fixed inset-y-0 left-0 flex flex-col bg-secondary-800 text-white shadow-xl md:shadow-none',
            'transition-transform duration-200 ease-out md:transition-all',
            collapsed ? 'md:w-16' : 'md:w-60',
            // Mobile sidebar width: clamp to [260px, 85vw, 320px].
            'w-[min(85vw,320px)]',
            mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          )}
          role="complementary"
          aria-label={t('sidebar.aria_label')}
          aria-hidden={mobileOpen ? undefined : 'true'}
        >
          {/* Brand */}
          <div
            className={cn(
              'flex items-center h-14 px-4 border-b border-white/10',
              collapsed && 'justify-center',
            )}
          >
            {!collapsed && <LogoSvg variant="symbol" className="h-7 w-auto" />}
            {collapsed && <LogoSvg variant="symbol" className="h-6 w-auto" />}
          </div>

          {/* Nav */}
          <nav
            className={cn(
              'flex-1 min-h-0 overflow-y-auto py-3 px-2',
              // White-thumb scrollbar for the dark blue sidebar surface.
              'scrollbar-subtle-dark',
            )}
            role="navigation"
            aria-label={t('sidebar.aria_label')}
          >
            <ul className="list-none space-y-1 p-0 m-0">
              {visibleSections.map((section, sectionIndex) => (
                <li key={section.label} className="list-none">
                  {sectionIndex > 0 && (
                    <div className="my-3 border-t border-white/10" role="separator" />
                  )}
                  {!collapsed && (
                    <span className="px-3 text-xs font-body font-medium text-white/40 uppercase tracking-wider">
                      {section.label}
                    </span>
                  )}
                  <ul className="list-none mt-1 space-y-0.5 p-0 m-0">
                    {section.items.map((item) => (
                      <li key={item.path} className="list-none">
                        <SidebarLink
                          item={item}
                          collapsed={collapsed}
                          t={t}
                          allPaths={allNavPaths}
                          onNavigate={() => setMobileOpen(false)}
                        />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>

            {/* ADMINISTRACAO sub-groups.
              - Expanded: business-group collapsible renderer with labels + sub-items.
              - Collapsed: icon-rail per group (SidebarAdminSubGroups handles the mode). */}
            {hasVisibleAdminGroups && (
              <div className="mt-3">
                <div className="my-3 border-t border-white/10" role="separator" />
                {!collapsed && (
                  <span className="px-3 text-xs font-body font-medium text-white/40 uppercase tracking-wider">
                    {t('sidebar.section_admin')}
                  </span>
                )}
                <div className="mt-1">
                  <SidebarAdminSubGroups collapsed={collapsed} />
                </div>
              </div>
            )}
          </nav>

          {/* Consultant level badge (visible for consultant roles) */}
          {user && !collapsed && (
            <div className="px-4 py-2 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-alamo-orange-500/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-alamo-orange-400 font-medium text-xs">
                    {user.name?.charAt(0).toUpperCase() ?? '?'}
                  </span>
                </div>
                <span className="text-xs font-body text-white/70 truncate">{user.name}</span>
              </div>
            </div>
          )}

          {/* Collapse toggle (desktop) */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="hidden md:flex items-center justify-center h-10 border-t border-white/10 text-white/50 hover:text-white transition-colors"
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            <ChevronLeft
              className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
            />
          </button>
        </aside>

        {/* Main content */}
        <div
          className={cn(
            // min-w-0 é OBRIGATÓRIO em flex items: sem ele o default
            // `min-width: auto` permite que o conteúdo intrínseco da page
            // (ex: kanban com 7×320px) estique a coluna inteira e gere
            // scroll-x na viewport. Bug UAT 2026-05-06.
            'flex min-w-0 flex-1 flex-col transition-all duration-200',
            collapsed ? 'md:ml-16' : 'md:ml-60',
          )}
        >
          {/* Banner de ativação (pré-aprovação) — vive DENTRO da coluna de
              conteúdo para nunca ficar atrás do sidebar fixed. */}
          {!isApproved && <PreApprovalBanner />}

          {/* Header */}
          <header
            role="banner"
            className="flex items-center justify-between h-14 gap-3 px-4 sm:px-6 lg:px-8 border-b border-slate-200 bg-white"
          >
            {/* Mobile menu button (44×44 tap target) */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className={cn(
                'md:hidden inline-flex items-center justify-center shrink-0',
                'size-11 -ml-2 rounded-lg',
                'text-slate-600 hover:text-secondary-950 hover:bg-slate-100',
                'focus:outline-none focus:ring-2 focus:ring-primary-500',
                'transition-colors',
                mobileOpen && 'hidden',
              )}
              aria-label={t('sidebar.toggle')}
              aria-expanded={mobileOpen}
            >
              <Menu className="size-5" aria-hidden />
            </button>

            {/* Mobile close */}
            {mobileOpen && (
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'md:hidden inline-flex items-center justify-center shrink-0',
                  'size-11 -ml-2 rounded-lg',
                  'text-slate-600 hover:text-secondary-950 hover:bg-slate-100',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500',
                  'transition-colors',
                )}
                aria-label={t('common:actions.close')}
              >
                <X className="size-5" aria-hidden />
              </button>
            )}

            {/* Brand logo - visible on desktop */}
            <div className="hidden md:flex items-center mr-3">
              <LogoSvg variant="symbol" className="h-6 w-auto opacity-60" />
            </div>

            {/* Breadcrumb -- driven by useMatches() + handle.breadcrumb per route (D-11) */}
            <div className="flex-1 min-w-0 flex items-center">
              <BreadcrumbHost />
            </div>

            {/* Notification bell + User menu dropdown (replaces inline profile link + logout per D-13) */}
            <div className="flex items-center gap-3 shrink-0">
              <NotificationBellDropdown />
              <UserMenuDropdown />
            </div>
          </header>

          {/* Nudge de instalação do PWA no login (não-fixo): toast + item no menu
              do avatar substituem o antigo botão fixo da topbar. Retorna null. */}
          <InstallAppPrompt />

          {/* Page content -- overflow-y-auto for normal pages, table pages override with h-full overflow-hidden.
            min-w-0 evita que conteúdo intrínseco grande (kanban) gere scroll-x global. */}
          <main role="main" className="min-w-0 flex-1 min-h-0 overflow-y-auto scrollbar-subtle">
            <div className="animate-page-enter min-w-0 px-4 md:px-6">
              <Outlet />
            </div>
          </main>
        </div>
        {/* W3.7-polish — welcome tour. Renders nothing; mounts driver.js when
          the user has tour_completed_at = null + tour.enabled = true. */}
        <TourProvider />
        {/* ESCR-269.5 — carrossel de login de comunicados de destaque (1x/sessão). */}
        <AnnouncementLoginCarousel />
        {/* Blocking re-acceptance overlay — self-mounts only when
          useAuth().user.pending_reacceptance is non-null. */}
        <ReacceptanceModal />
        {/* Blocking data-reconfirmation overlay — self-mounts only when
          useAuth().user.requiresDataConfirmation is true (and no consent /
          reacceptance is pending). Mounted after the consent redirect so the
          consent gate keeps route priority. */}
        <DataReconfirmationProvider />
      </div>
    </BreadcrumbProvider>
  );
}

function SidebarLink({
  item,
  collapsed,
  t,
  allPaths,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  t: (key: string) => string;
  allPaths: string[];
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const translatedLabel = t(item.label);

  // isActive is computed manually (not via NavLink's render-prop) because
  // Radix Tooltip.Trigger asChild wraps this link in a Slot, which cannot
  // forward NavLink's function-shaped className / children. The previous
  // implementation serialized the render-prop function as a literal string
  // into the DOM `class` attribute, producing ghost classes and stray marks.
  const location = useLocation();
  const { pathname } = location;
  // A parent path (e.g. /team) matches by prefix UNLESS a more specific sibling
  // (e.g. /team/referrals) is itself a prefix of the current route — that more
  // specific item owns the active state instead.
  const matchesByPrefix =
    pathname === item.path ||
    (pathname.startsWith(`${item.path}/`) &&
      !allPaths.some(
        (p) =>
          p !== item.path &&
          p.startsWith(`${item.path}/`) &&
          (pathname === p || pathname.startsWith(`${p}/`)),
      ));
  const isActive = matchesByPrefix;

  const link = (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      className={cn(
        'flex min-w-0 items-center gap-3 rounded-lg px-3 py-2 text-sm font-body transition-colors',
        collapsed && 'justify-center px-0',
        isActive
          ? 'bg-alamo-orange-500/15 text-alamo-orange-400 border-l-[3px] border-alamo-orange-500 font-medium'
          : 'text-white/70 hover:bg-white/10 hover:text-white',
      )}
    >
      <Icon
        className={cn('h-5 w-5 shrink-0', isActive ? 'text-alamo-orange-400' : 'text-white/60')}
      />
      {!collapsed && (
        <>
          <span className="truncate" title={translatedLabel}>
            {translatedLabel}
          </span>
          {item.badge === 'coming_soon' && (
            <Badge
              variant="neutral"
              size="sm"
              className="ml-auto shrink-0 bg-white/10 text-white/70"
            >
              {t('common:badges.coming_soon')}
            </Badge>
          )}
        </>
      )}
    </NavLink>
  );

  // Show a fly-out with the label when sidebar is collapsed. Flat items
  // (no sub-routes) render as a tooltip-style popover; the trigger is the
  // NavLink itself so click still works even if the user never hovers.
  if (collapsed) {
    return <SidebarFlyout label={translatedLabel}>{link}</SidebarFlyout>;
  }

  return link;
}
